<script setup lang="ts">
    import { Quill, QuillEditor } from '@vueup/vue-quill';
    // import '@vueup/vue-quill/dist/vue-quill.bubble.css';
    import '@vueup/vue-quill/dist/vue-quill.snow.css';

    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    const { $theme } = useNuxtApp();

    const fontSizes = ref(['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '60px', '72px', '96px']);
    const fontSizePlugin = Quill.imports['attributors/style/size'];
    fontSizePlugin.whitelist = fontSizes.value;
    Quill.register(fontSizePlugin, true);

    // Force https:// on schemeless links so they aren't treated as relative.
    const LinkFormat = Quill.import('formats/link');
    class AbsoluteLink extends LinkFormat {
        static sanitize(url: string) {
            const value = (url ?? '').trim();
            if (value && !/^[a-z][a-z\d+.-]*:/i.test(value) && !value.startsWith('//') && !value.startsWith('#')) {
                return super.sanitize(`https://${value}`);
            }
            return super.sanitize(url);
        }
    }
    Quill.register(AbsoluteLink, true);

    interface EditorFormInputProps extends FormInputProps {
        variablesSubmenuPosition?: string;
        withoutVariables?: boolean;
        withTemplateVariables?: boolean;
        disabled?: boolean;
        // Whitelist of allowed Quill formats; undefined = full toolbar.
        formats?: string[];
        /**
         * The frame drawn around the Quill area — border, radius, shadow.
         *
         * ADDITIVE: left out, the editor keeps the exact chrome it has always had, so
         * every existing call site is unchanged. The mobile document form passes the
         * design's own box instead, because its editors sit inches from fields drawn
         * with an 8px radius and a zinc border and the 6px gray-200 frame read as a
         * different component.
         *
         * Not `containerClassName`: that name is already taken by `useFormInput`, where
         * it styles the wrapper around the whole field, label included.
         */
        innerContainerClassName?: string;
    }

    const props = withDefaults(defineProps<EditorFormInputProps>(), {
        elementClassName: 'pb-6',
        variablesSubmenuPosition: 'right-0',
        withoutVariables: false,
        disabled: false,
        withTemplateVariables: false,
        formats: undefined,
        innerContainerClassName: '!shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] !rounded-md !border !border-gray-200',
    });

    const emit = defineEmits<{
        (e: 'update:translated', value: string): void;
        // Relayed from FormElement so a parent can drive several translated
        // fields from one locale (`v-model:locale`, TDD-3784).
        (e: 'update:locale', value: string): void;
    }>();

    const inputData: FormInputData = useFormInput(props, {
        className: $theme('form.textarea'),
    });

    // Link button is opt-in (must be in `formats`); other controls show unless excluded.
    const isFormatAllowed = (name: string) => !props.formats || props.formats.includes(name);
    const withLink = computed(() => props.formats?.includes('link') ?? false);

    const setModel = (content: string) => {
        const cleaned = removeScriptTags(content).trim();

        // Check for real content
        const hasMeaningfulContent = containsVisibleText(cleaned);

        const finalContent = hasMeaningfulContent ? cleaned : '';

        inputData.model.value = finalContent;

        if (inputData.translated) {
            emit('update:translated', finalContent);
        }
    };

    function removeScriptTags(html: string): string {
        return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
    }

    function containsVisibleText(html: string): boolean {
        const div = document.createElement('div');
        div.innerHTML = html;

        const text = div.textContent || div.innerText || '';
        return text.replace(/\u00A0/g, '').trim().length > 0;
    }

    const colors = useColors();
    const setColor = (color: string) => {
        quillEditor.value?.getQuill().format('color', color);
    };

    const setFontSize = (fontSize: string) => {
        quillEditor.value?.getQuill().format('size', fontSize);
    };

    const addVariable = (variable: string) => {
        const value = inputData.model.value;
        quillEditor.value?.setContents(`${value}%${variable}%`);
    };

    const id = `editor-${Math.random().toString(36).substr(2, 9)}`;
    const quillEditor = ref(null);
    // Pass `formats` to Quill so disallowed formats are also stripped on paste.
    const quillOptions = computed(() => ({
        bounds: `#${id}`,
        ...(props.formats ? { formats: props.formats } : {}),
    }));

    const disableEditorIfNeeded = () => {
        if (props.disabled) {
            quillEditor.value?.getQuill()?.disable();
            return;
        }

        quillEditor.value?.getQuill()?.enable();
    };

    // `disabled` is read once on mount below, which is enough while it is a constant.
    // It is not on a document form: `form.isDisabled()` follows the invoice's own
    // `isEditable`, which arrives with the model and can flip while the editor is
    // already mounted — the editor then stayed writable on a locked document.
    watch(
        () => props.disabled,
        () => disableEditorIfNeeded()
    );

    onMounted(async () => {
        const container = document.getElementById(id);
        const input = document.querySelector('.ql-editor');
        if (input && container) {
            input.addEventListener('focus', () => {
                container.classList.add('ql-focused');
            });
            input.addEventListener('blur', () => {
                container.classList.remove('ql-focused');
            });
        }
        if (props.autofocus && input) {
            await wait(250);
            (input as HTMLElement).focus();
        }
        disableEditorIfNeeded();

        // var toolbarElement = document.getElementById(`${id}-toolbar`);
        // var observer = new MutationObserver(function(mutations) {
        //     if (document.contains(toolbarElement)) {
        //         console.log("It's in the DOM!");
        //         observer.disconnect();
        //     } else {
        //         console.log("It's not in the DOM!");
        //     }
        // });
        // observer.observe(document, {attributes: false, childList: true, characterData: false, subtree:true});
    });
</script>

<template>
    <FormElement
        :id="id"
        :input-data="inputData"
        class="relative editor-container group/editor-container"
        @update:locale="
            emit('update:translated', inputData.model.value);
            emit('update:locale', $event);
        "
    >
        <slot name="before-input" />
        <div
            class="relative editor-inner-container"
            :class="innerContainerClassName"
        >
            <QuillEditor
                ref="quillEditor"
                :key="inputData.elementKey.value"
                theme="snow"
                :content="inputData.model.value"
                :options="quillOptions"
                content-type="html"
                :toolbar="`#${id}-toolbar`"
                @update:content="setModel"
            >
                <template #toolbar>
                    <div
                        :id="`${id}-toolbar`"
                        class="flex gap-2 p-1 ql-editor-toolbar relative"
                    >
                        <div v-if="isFormatAllowed('size')">
                            <Submenu relative-div-class="h-[24px]">
                                <template #default="{ toggle }">
                                    <slot :toggle="toggle">
                                        <button
                                            class="flex items-start justify-center !pt-0 ql-fontsize ql-picker ql-fontsize-picker"
                                            :data-tooltip="$t('misc.editor.fontSize')"
                                            @click.prevent="toggle"
                                        >
                                            <i class="text-sm fa-regular fa-text-size"></i>
                                        </button>
                                    </slot>
                                </template>
                                <template #submenu="{ hide }">
                                    <div :class="$theme('submenu.items')">
                                        <span
                                            v-for="fontSize in fontSizes"
                                            :key="fontSize"
                                            :class="$theme('submenu.item', 'cursor-pointer')"
                                            @click.prevent="
                                                setFontSize(fontSize);
                                                hide();
                                            "
                                        >
                                            {{ fontSize }}
                                        </span>
                                    </div>
                                </template>
                            </Submenu>
                        </div>
                        <div>
                            <button
                                v-if="isFormatAllowed('bold')"
                                class="ql-bold"
                                :data-tooltip="$t('misc.editor.bold')"
                            ></button>
                            <button
                                v-if="isFormatAllowed('italic')"
                                class="ql-italic"
                                :data-tooltip="$t('misc.editor.italic')"
                            ></button>
                            <button
                                v-if="isFormatAllowed('underline')"
                                class="ql-underline"
                                :data-tooltip="$t('misc.editor.underline')"
                            ></button>
                            <button
                                v-if="isFormatAllowed('strike')"
                                class="ql-strike"
                                :data-tooltip="$t('misc.editor.strike')"
                            ></button>
                            <button
                                v-if="withLink"
                                class="ql-link"
                                :data-tooltip="$t('misc.editor.link')"
                            ></button>
                        </div>
                        <div v-if="isFormatAllowed('list')">
                            <button
                                class="ql-list"
                                value="ordered"
                                :data-tooltip="$t('misc.editor.orderedList')"
                            ></button>
                            <button
                                class="ql-list"
                                value="bullet"
                                :data-tooltip="$t('misc.editor.bulletList')"
                            ></button>
                        </div>
                        <div
                            v-if="isFormatAllowed('color')"
                            class="flex items-center"
                        >
                            <Submenu relative-div-class="h-[24px]">
                                <template #default="{ toggle }">
                                    <slot :toggle="toggle">
                                        <button
                                            class="ql-color ql-picker ql-color-picker"
                                            :data-tooltip="$t('misc.editor.color')"
                                            @click.prevent="toggle"
                                        ></button>
                                    </slot>
                                </template>
                                <template #submenu="{ hide }">
                                    <div class="flex flex-wrap gap-2 w-[240px] py-2">
                                        <div
                                            v-for="color in colors"
                                            :key="color"
                                        >
                                            <!-- `role="button"` is load-bearing, not decoration. Inside a
                                                 mobile sheet the drawer's swipe-to-dismiss handler claims
                                                 every pointer that does not land on
                                                 `button,a,input,select,textarea,label,[role="button"]`
                                                 (reka-ui's own `DEFAULT_IGNORE_SELECTOR`): it captures the
                                                 pointer on `pointerdown`, so `pointerup` and the `click`
                                                 retarget to the drawer and the swatch never fires. Picking a
                                                 colour was impossible on a phone. Nothing about the
                                                 rendering changes, so desktop is untouched. -->
                                            <span
                                                role="button"
                                                tabindex="0"
                                                :aria-label="color"
                                                class="block w-6 h-6 border-2 border-white rounded-full cursor-pointer hover:shadow"
                                                :style="`background: ${color};`"
                                                @click.prevent="
                                                    setColor(color);
                                                    hide();
                                                "
                                                @keydown.enter.prevent="
                                                    setColor(color);
                                                    hide();
                                                "
                                                @keydown.space.prevent="
                                                    setColor(color);
                                                    hide();
                                                "
                                            ></span>
                                        </div>
                                    </div>
                                </template>
                            </Submenu>
                        </div>
                    </div>
                </template>
            </QuillEditor>
            <div
                v-if="!withoutVariables"
                class="absolute top-0 right-0 hidden pt-2 pr-2 group-hover/editor-container:block"
            >
                <span class="h-[26px] w-[26px] pt-[3px] rounded-full bg-neutral-100 flex items-center justify-center">
                    <FormPartialsVariablesSubmenu
                        :submenu-position="variablesSubmenuPosition"
                        :with-template-variables="withTemplateVariables"
                        icon-color="text-branding-rich-black"
                        @add-variable="addVariable"
                    />
                </span>
            </div>
        </div>
        <slot name="after-input" />
    </FormElement>
</template>

<style>
    .ql-editor {
        min-height: 140px;
        max-height: 240px;
        /* border: 1px solid #e5e5e5; */
        border-radius: 4px;
        transition: all 0.3s linear;
        background: white;
        /* Prevent iOS auto-zoom on focus (requires font-size >= 16px) */
        font-size: 16px;
    }
    .ql-toolbar.ql-snow {
        border: none;
        border-bottom: 1px solid #e5e5e5;
    }
    .ql-container.ql-snow {
        max-width: 1300px;
        border: none;
    }
    .ql-bubble .ql-tooltip {
        background-color: #edf2fb;
        transition: background-color 0.3s linear;
    }
    .ql-bubble .ql-tooltip:hover {
        background-color: #edf2fb;
    }
    .ql-bubble .ql-tooltip:not(.ql-flip) .ql-tooltip-arrow {
        border-bottom-color: #edf2fb;
        transition: border-bottom-color-color 0.3s linear;
    }
    .ql-bubble .ql-tooltip:hover:not(.ql-flip) .ql-tooltip-arrow {
        border-bottom-color: #edf2fb;
    }
    .ql-bubble .ql-stroke {
        stroke: #0d00e8;
    }
    .ql-bubble .ql-toolbar button {
        transition: all 0.3s linear;
        border-radius: 4px;
    }
    .ql-bubble .ql-toolbar button.ql-picker {
        color: #0d00e8;
    }
    .ql-bubble .ql-toolbar button .ql-fill {
        fill: #0d00e8;
    }
    .ql-bubble .ql-toolbar button:hover .ql-bubble .ql-toolbar button.ql-active {
        background: #ccdbfd;
    }

    .ql-bubble .ql-toolbar button.ql-picker:hover,
    .ql-bubble .ql-toolbar button.ql-active.ql-picker {
        color: #0c0672;
    }
    .ql-bubble .ql-toolbar button:hover .ql-stroke,
    .ql-bubble .ql-toolbar button.ql-active .ql-stroke {
        stroke: #0c0672;
    }
    .ql-bubble .ql-toolbar button:hover .ql-fill,
    .ql-bubble .ql-toolbar button.ql-active .ql-fill {
        fill: #0c0672;
    }

    .editor-container.ql-focused {
        position: relative;
        z-index: 1;
    }

    .editor-inner-container.ql-focused {
        /* border-color: black !important; */
        /* box-shadow: 0 0 0 1px black !important; */
    }

    .editor-container.ql-focused .editor-inner-container.ql-focused {
        /* border-color: black !important; */
        /* box-shadow: 0 0 0 1px black !important; */
    }

    .editor-container.ql-focused .ql-container.ql-snow {
        /* border-color: black !important; */
    }

    .editor-container.ql-focused .editor-inner-container {
        outline: 1px solid black !important;
        transition: all 0.3s ease-out;
    }

    .editor-container:hover {
        z-index: 9;
    }
</style>
